<template>
	<div
		ref="nodeMixCompContainer"
		class="mix-composition"
	>
		<!-- nasty hack: data attributes solely to force Vue to run update; not actually used -->
		<canvas
			ref="nodeMixCompCanvas"
			class="mix-composition__canvas"
			:data-color-data="JSON.stringify( colorsForMixComp )"
		/>
	</div>
</template>


<style lang="scss">
.mix-composition {
	position: relative;
	display: block;
	width: var( --diameter, 100% );
	max-width: 100%;
	padding: 0; // 0 calc( 100% - 32px ) 0;
	margin-left: auto;
	margin-right: auto;
	aspect-ratio: 1;
	border: 16px solid white;
	box-shadow: 0px 5px 15px 10px rgba(0,0,0,0.1);
	border-radius: 50%;
	overflow: hidden;

	@media( min-width: 768px ) {
		border-width: 24px;
	}

	&__canvas {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		border-radius: 50%;
	}
}
</style>


<script setup>
import { computed, onMounted, onUpdated, ref, watch } from 'vue'
import { MixComp } from '../classes/MixComp'


const props = defineProps({
	colorData: {
		type: Array,
		required: true,
	},
	scrollToMixer: {
		type: Number,
		required: true,
	},
})


const nodeMixCompContainer = ref( null )
const nodeMixCompCanvas = ref( null )

let colorMixAsString = ''
let remixTimeout = null


// eslint-disable-next-line arrow-body-style
const colorsForMixComp = computed( () => {
	return props.colorData
		.filter( color => 0 < color.percentage )
		.map( ({ id, pebbles, percentage }) => ({
			id,
			percentage,
			images: pebbles,
		}) )
})


// value will change when user clicks button to scroll to mixer
// eslint-disable-next-line consistent-return
watch( () => props.scrollToMixer, () => {

	// zero will be on page load (don't scroll in this case)
	if ( 0 === props.scrollToMixer ) {
		return null
	}

	let offsetY = nodeMixCompContainer.value.getBoundingClientRect().top + window.scrollY

	const nodeHeader = document.getElementById( 'page-header' )
	if ( nodeHeader ) {
		offsetY -= nodeHeader.getBoundingClientRect().height
	}

	window.scrollTo({
		top: offsetY,
		behavior: 'smooth',
	})
})


/**
 * Trigger mix comp to render pebbles
 *
 * @return {void}
 */
const renderMixComp = () => {
	const mixComp = window?.webfx?.colorMixer?.mixComp

	// was mix comp object initiated?
	if ( !mixComp ) {
		return
	}

	// do we have a total of 100% (or 99% in case of three colors)?
	const totalColors = colorsForMixComp.value.length
	const totalPercentage = colorsForMixComp.value.reduce( ( total, color ) => total + color.percentage, 0 )

	// are we clearing mix comp render?
	if ( 0 === totalColors ) {
		mixComp.clearPebbles()
		return
	}

	if ( 3 === totalColors ) {
		if ( 99 !== totalPercentage && 100 !== totalPercentage ) {
			return
		}
	} else {
		// eslint-disable-next-line no-lonely-if
		if ( 100 !== totalPercentage ) {
			return
		}
	}

	mixComp.setColorData( colorsForMixComp.value )
	mixComp.mixColors()
}


onMounted( () => {
	const diameter = window.innerHeight - document.getElementById( 'page-header' ).getBoundingClientRect().height - 50

	// entire mix comp should always be visible (e.g. if top of comp is at top of browser, shouldn't extend past view)
	nodeMixCompContainer.value.style.setProperty( '--diameter', `${diameter}px` )

	window.webfx = window.webfx || {}
	window.webfx.colorMixer = window.webfx.colorMixer || {}

	// for performance purposes, keeping MixComp/MatterJS outside of Vue's reactivity
	window.webfx.colorMixer.mixComp = new MixComp( nodeMixCompCanvas.value, nodeMixCompContainer.value )

	colorMixAsString = JSON.stringify( colorsForMixComp.value )

	renderMixComp()
})


onUpdated( () => {

	// avoid unnecessary re-rendering
	const colorMixAsStringNew = JSON.stringify( colorsForMixComp.value )

	if ( colorMixAsString === colorMixAsStringNew ) {
		return
	}

	colorMixAsString = colorMixAsStringNew

	// debounce
	clearTimeout( remixTimeout )

	remixTimeout = setTimeout( renderMixComp, 500 )
})

</script>
